import Tooltip from "@mui/material/Tooltip";
import {
  formatLanguageName,
  normalizeSupportedLanguages,
} from "../../../utils/composerLanguages";

const STATE_GLYPH = { all: "✓", none: "○", partial: "◐" };

/**
 * Compact per-language activation roll-up for a single content-tree row. It occupies one small
 * pill regardless of how many languages the course has, and double-encodes state as BOTH color and
 * symbol (colorblind-safe, legible at 18px):
 *   - all supported languages active  -> green  "✓"
 *   - all draft                       -> grey   "○"
 *   - some active, some draft          -> amber  "◐"  (only possible with >1 language)
 * Hover/focus reveals the full per-language breakdown ("English: Active · Hebrew: Draft").
 *
 * Renders nothing when the owning course has no supported languages (nothing to summarize). Used on
 * every in-scope tree node (course root, units, lessons, scenarios, info-pages, steps, states, skills);
 * out-of-scope rows (lesson versions, scenario conditions, annotations, questions) never get one.
 *
 * @param {object} props
 * @param {Object.<string,boolean>} props.activeByLanguage - the node's per-language activation map
 * @param {string[]} props.supportedLanguages - the owning course's supported languages (the chip set)
 */
const NodeActivationBadge = ({ activeByLanguage, supportedLanguages }) => {
  // Nothing to summarize without a real language set. Guard the RAW input: normalizeSupportedLanguages
  // defaults [] -> ["english"], which would otherwise paint a spurious English chip on data-less nodes.
  if (!Array.isArray(supportedLanguages) || supportedLanguages.length === 0) return null;
  const languages = normalizeSupportedLanguages(supportedLanguages);

  const map = activeByLanguage || {};
  const activeCount = languages.reduce((n, lang) => n + (map[lang] ? 1 : 0), 0);
  const total = languages.length;
  const state = activeCount === total ? "all" : activeCount === 0 ? "none" : "partial";
  const detail = languages
    .map((lang) => `${formatLanguageName(lang)}: ${map[lang] ? "Active" : "Draft"}`)
    .join(" · ");

  return (
    <Tooltip title={detail} placement="top" arrow enterDelay={300} disableInteractive>
      <span
        className={`pd-node-activation pd-node-activation--${state}`}
        data-testid="node-activation-badge"
        data-state={state}
        data-active-count={activeCount}
        data-total={total}
        role="img"
        aria-label={`Activation — ${detail}`}
      >
        <span aria-hidden="true">{STATE_GLYPH[state]}</span>
      </span>
    </Tooltip>
  );
};

export default NodeActivationBadge;
